import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Paper,
    Grid,
    Alert,
    Divider
} from '@mui/material';
import {
    Key as KeyIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

export const ApiKeyManagementInterface: React.FC = () => {
    const { user } = useAuth();
    const [apiKeys, setApiKeys] = useState<{test?: string, production?: string}>({});
    const [showTestKey, setShowTestKey] = useState(false);
    const [showProductionKey, setShowProductionKey] = useState(false);
    const [isGeneratingKey, setIsGeneratingKey] = useState(false);

    // Load persisted API keys on mount
    React.useEffect(() => {
        const savedKeys = localStorage.getItem('admin-api-keys');
        if (savedKeys) {
            try {
                setApiKeys(JSON.parse(savedKeys));
            } catch (error) {
                console.error('Failed to load saved API keys:', error);
            }
        }
    }, []);

    // Save API keys to localStorage whenever they change
    React.useEffect(() => {
        if (Object.keys(apiKeys).length > 0) {
            localStorage.setItem('admin-api-keys', JSON.stringify(apiKeys));
        }
    }, [apiKeys]);

    const generateApiKey = async (type: 'test' | 'production') => {
        if (!user) return;
        
        setIsGeneratingKey(true);
        try {
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            
            if (!token) {
                console.error('No authentication token available');
                return;
            }

            const response = await fetch('/api/v1/admin/regenerate-key', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    keyType: type,
                    userEmail: user.email
                }),
            });
            
            const data = await response.json();
            if (data.success && data.apiKey) {
                setApiKeys(prev => ({
                    ...prev,
                    [type]: data.apiKey
                }));
            } else {
                console.error('Failed to generate API key:', data.error || data.message);
            }
        } catch (error) {
            console.error('Failed to generate API key:', error);
        } finally {
            setIsGeneratingKey(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const maskApiKey = (key: string) => {
        if (key.length <= 8) return key;
        return key.substring(0, 4) + '•'.repeat(key.length - 8) + key.substring(key.length - 4);
    };

    return (
        <Box sx={{ p: 2, maxHeight: 'none', overflow: 'visible' }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <KeyIcon color="primary" />
                API Key Management
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Generate and manage API keys for testing and production use. Test keys are for development 
                and have limited functionality, while production keys provide full API access.
            </Typography>

            <Grid container spacing={3}>
                {/* Test/Sandbox API Key */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Test API Key (Sandbox)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Use this key for development and testing. Limited to sandbox data and functionality.
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Test API Key"
                                value={apiKeys.test ? (showTestKey ? apiKeys.test : maskApiKey(apiKeys.test)) : 'No key generated'}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: apiKeys.test && (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowTestKey(!showTestKey)}
                                            >
                                                {showTestKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => copyToClipboard(apiKeys.test!)}
                                            >
                                                <ContentCopyIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                }}
                            />
                        </Box>
                        
                        <Button
                            variant="outlined"
                            startIcon={apiKeys.test ? <RefreshIcon /> : <KeyIcon />}
                            onClick={() => generateApiKey('test')}
                            disabled={isGeneratingKey}
                        >
                            {apiKeys.test ? 'Regenerate Test Key' : 'Generate Test Key'}
                        </Button>
                    </Paper>
                </Grid>

                {/* Production API Key */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom color="error">
                            Production API Key
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Use this key for production applications. Provides full API access and real data processing.
                        </Typography>
                        
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Admin Only:</strong> Production keys have full system access. Keep them secure and never share them publicly.
                            </Typography>
                        </Alert>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Production API Key"
                                value={apiKeys.production ? (showProductionKey ? apiKeys.production : maskApiKey(apiKeys.production)) : 'No key generated'}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: apiKeys.production && (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowProductionKey(!showProductionKey)}
                                            >
                                                {showProductionKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => copyToClipboard(apiKeys.production!)}
                                            >
                                                <ContentCopyIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                }}
                            />
                        </Box>
                        
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={apiKeys.production ? <RefreshIcon /> : <KeyIcon />}
                            onClick={() => generateApiKey('production')}
                            disabled={isGeneratingKey}
                        >
                            {apiKeys.production ? 'Regenerate Production Key' : 'Generate Production Key'}
                        </Button>
                    </Paper>
                </Grid>

                {/* Usage Instructions */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            How to Use Your API Key
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Include your API key in the request headers for all API calls:
                        </Typography>
                        
                        <Paper sx={{ p: 2, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            <Typography component="pre" sx={{ margin: 0 }}>
{`curl -X POST "http://localhost:3001/api/v1/submit-quote" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your-api-key-here" \\
  -d '{"ProductNumber": "SAIL-001", "RequestId": "REQ-123"}'`}
                            </Typography>
                        </Paper>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" gutterBottom>
                            Available Endpoints:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                <strong>POST</strong> /api/v1/submit-quote - Submit a new quote request
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                <strong>GET</strong> /api/v1/retrieve-price/:productNumber - Get quote pricing
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                <strong>POST</strong> /api/v1/confirm-order - Confirm and accept a quote
                            </Typography>
                            <Typography component="li" variant="body2">
                                <strong>GET</strong> /api/v1/check-status/:productNumber - Check quote status
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};